import { getAccessToken } from '@/application/auth/services/tokenService'
import { API_URI } from '@/constants'

const BACKEND_URL = API_URI

export type ApiError<Error = unknown> = {
  status: number
  statusText: string
  data?: Error
  message: string
}

export type ErrorType<Error> = ApiError<Error>

export const isApiError = (error: unknown): error is ApiError<unknown> => {
  if (!error || typeof error !== 'object') {
    return false
  }

  return 'status' in error && 'statusText' in error && 'message' in error
}

const getErrorMessage = (data: unknown, fallback: string): string => {
  if (typeof data === 'string' && data.trim().length > 0) {
    return data
  }

  if (data && typeof data === 'object' && 'message' in data && typeof (data as { message?: unknown }).message === 'string') {
    return (data as { message: string }).message
  }

  return fallback || 'Request failed'
}

const parseErrorData = async (response: Response): Promise<unknown> => {
  try {
    return await response.clone().json()
  } catch {
    try {
      const text = await response.text()
      return text ? { message: text } : undefined
    } catch {
      return undefined
    }
  }
}

export const customInstance = async <T>(config: {
  url: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  params?: Record<string, unknown>
  data?: BodyInit | object
  headers?: HeadersInit
  signal?: AbortSignal
}): Promise<T> => {
  const { url, method, params, data, headers, signal } = config

  const searchParams = new URLSearchParams()
  if (params) {
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null)
      .forEach(([key, value]) => searchParams.append(key, String(value)))
  }
  const queryString = searchParams.toString() ? `?${searchParams.toString()}` : ''

  const body = data && !(data instanceof FormData) && typeof data === 'object' ? JSON.stringify(data) : (data as BodyInit | undefined)

  const accessToken = await getAccessToken()

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json'
  }

  if (accessToken) {
    requestHeaders.Authorization = `Bearer ${accessToken}`
  }

  if (import.meta.env.DEV) {
    requestHeaders['X-API-KEY'] = 'localApiKeyUi'
  }

  const response = await fetch(`${BACKEND_URL}${url}${queryString}`, {
    method,
    headers: {
      ...requestHeaders,
      ...headers
    },
    body,
    signal
  })

  if (!response.ok) {
    const data = await parseErrorData(response)
    const message = getErrorMessage(data, response.statusText)

    throw Object.assign(new Error(message), {
      status: response.status,
      statusText: response.statusText,
      data,
      message
    })
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}
